/**
 * Collection infinite scroll via Shopify Section Rendering API.
 *
 * Fetches the next paginated page as server-rendered section HTML
 * (`?page=2&sections=template`), appends product grid items, and
 * dispatches `shopify:section:load` so apps can re-initialize.
 *
 * Loaded pages, scroll position and the last opened product are stored in
 * `history.state` so Back from a product page restores the same products.
 * The current page is also written to the URL (`?page=2`) so it can be shared.
 * Visiting `?page=N` prepends pages 1..N-1 so the shared link shows the same products.
 *
 * Markup: see `sections/template-collection.liquid`
 */
class CollectionInfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this._loading = false;
    this._restoring = false;
    this._observer = null;
    this._prefersReducedMotion = false;
    this._abort = null;
    this._onGridClick = this._onGridClick.bind(this);
    this._onPageHide = this._onPageHide.bind(this);
  }

  connectedCallback() {
    if (this.ownerDocument !== document) return;

    this.sectionId = this.dataset.sectionId;
    this.grid = this.querySelector('.collection-products-grid');
    this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
    this.statusEl = this.querySelector('[data-infinite-scroll-status]');
    this.loadMoreBtn = this.querySelector('[data-infinite-scroll-load-more]');
    this.loadingEl = this.querySelector('[data-infinite-scroll-loading]');
    this.errorEl = this.querySelector('[data-infinite-scroll-error]');
    this.retryBtn = this.querySelector('[data-infinite-scroll-retry]');

    this.nextUrl = this.dataset.nextUrl || null;
    this._loadedUntilPage = parseInt(this.dataset.currentPage, 10) || 1;
    this._prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._abort = new AbortController();

    if (!this.grid) return;

    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', () => this.loadNext());
    }

    if (this.retryBtn) {
      this.retryBtn.addEventListener('click', () => this.loadNext());
    }

    this.grid.addEventListener('click', this._onGridClick);
    window.addEventListener('pagehide', this._onPageHide);

    const saved = this._savedState();
    const pagesToPrepend = this._loadedUntilPage - 1;
    const pagesToRestore = saved ? saved.loadedUntilPage - this._loadedUntilPage : 0;

    if (
      pagesToPrepend > 0 ||
      pagesToRestore > 0 ||
      saved?.productId ||
      typeof saved?.scrollY === 'number'
    ) {
      this._restore(saved);
      return;
    }

    if (!this.nextUrl) {
      this._hideLoadControls();
      return;
    }

    this._showLoadMoreButton();
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._observer?.disconnect();
    this.grid?.removeEventListener('click', this._onGridClick);
    window.removeEventListener('pagehide', this._onPageHide);
  }

  _setupObserver() {
    if (!this.sentinel || !window.IntersectionObserver) {
      this._showLoadMoreButton();
      return;
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) this.loadNext();
      },
      { rootMargin: '600px 0px' }
    );
    this._observer.observe(this.sentinel);
  }

  async loadNext() {
    if (this._loading || this._restoring || !this.nextUrl) return;

    this._loading = true;
    this._setLoading(true);
    this._hideError();

    try {
      const page = await this._fetchPage(this.nextUrl);
      if (!page.items.length) {
        this._finishPagination();
        return;
      }

      this._appendItems(page.items);
      this._setNextUrl(page.nextUrl);
      this._loadedUntilPage += 1;
      this._saveState();

      if (!this.nextUrl) {
        this._finishPagination();
      }

      this._announce(`${page.items.length} extra producten geladen`);
      this._updatePageLabel();
      this._notifySectionLoad();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[collection-infinite-scroll]', err);
      this._showError();
      this._showLoadMoreButton();
    } finally {
      this._loading = false;
      this._setLoading(false);
    }
  }

  async _restore(saved) {
    this._restoring = true;
    this._hideError();

    const pagesToPrepend = Math.max(0, this._loadedUntilPage - 1);
    const pagesToRestore = saved ? Math.max(0, saved.loadedUntilPage - this._loadedUntilPage) : 0;
    const currentPageStart = this.grid.firstElementChild;
    const prevScrollRestoration = history.scrollRestoration;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    if (pagesToPrepend > 0 || (pagesToRestore > 0 && this.nextUrl)) {
      this._loading = true;
      this._setLoading(true);
      this._announce('Eerder geladen producten herstellen…');

      try {
        let addedCount = 0;

        if (pagesToPrepend > 0) {
          addedCount += await this._prependEarlierPages(this._loadedUntilPage);
        }

        while (saved && this._loadedUntilPage < saved.loadedUntilPage && this.nextUrl) {
          const page = await this._fetchPage(this.nextUrl);
          if (!page.items.length) {
            this._setNextUrl(null);
            break;
          }
          this._appendItems(page.items);
          this._setNextUrl(page.nextUrl);
          this._loadedUntilPage += 1;
          addedCount += page.items.length;
        }

        if (addedCount) this._notifySectionLoad();
      } catch (err) {
        if (err.name === 'AbortError') {
          if ('scrollRestoration' in history) {
            history.scrollRestoration = prevScrollRestoration || 'auto';
          }
          this._restoring = false;
          return;
        }
        console.error('[collection-infinite-scroll]', err);
        this._showError();
        this._showLoadMoreButton();
      } finally {
        this._loading = false;
        this._setLoading(false);
      }
    }

    if (saved) {
      this._restoreScroll(saved);
    } else if (pagesToPrepend > 0 && currentPageStart) {
      requestAnimationFrame(() => this._scrollToElement(currentPageStart));
    }

    this._saveState(saved ? { productId: null, scrollY: saved.scrollY } : { productId: null });
    if ('scrollRestoration' in history) {
      history.scrollRestoration = prevScrollRestoration || 'auto';
    }
    this._restoring = false;

    if (!this.nextUrl) {
      this._finishPagination();
      return;
    }

    this._updatePageLabel();
    this._showLoadMoreButton();
  }

  _restoreScroll(saved) {
    const apply = () => {
      if (saved.productId) {
        const card = this.grid.querySelector(`[data-product-id="${CSS.escape(String(saved.productId))}"]`);
        if (card) {
          this._scrollToElement(card);
          return;
        }
      }

      if (typeof saved.scrollY === 'number') {
        window.scrollTo({ top: saved.scrollY, behavior: 'auto' });
      }
    };

    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
  }

  _scrollToElement(element) {
    const offset = 80;
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  }

  async _prependEarlierPages(currentPage) {
    const earlier = [];
    for (let page = 1; page < currentPage; page += 1) {
      const result = await this._fetchPage(this._urlForPage(page));
      earlier.push(result.items);
    }

    const items = earlier.flat();
    this._prependItems(items);
    return items.length;
  }

  async _fetchPage(pageUrl) {
    const url = this._buildSectionUrl(pageUrl);
    const res = await fetch(url, {
      credentials: 'same-origin',
      signal: this._abort?.signal,
    });
    if (!res.ok) throw new Error(`Section fetch failed: ${res.status}`);

    const data = await res.json();
    const html = data[this.sectionId];
    if (!html) throw new Error('No section HTML in response');

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newScroll = doc.querySelector('collection-infinite-scroll');
    const newGrid =
      newScroll?.querySelector('.collection-products-grid') ?? doc.querySelector('.collection-products-grid');

    const items = newGrid ? Array.from(newGrid.children) : [];

    return {
      items,
      nextUrl: newScroll?.dataset.nextUrl || null,
    };
  }

  _appendItems(items) {
    this._insertItems(items, false);
  }

  _prependItems(items) {
    this._insertItems(items, true);
  }

  _insertItems(items, prepend) {
    if (!items.length) return;

    const fragment = document.createDocumentFragment();
    items.forEach((item) => fragment.appendChild(item));

    if (prepend) {
      this.grid.insertBefore(fragment, this.grid.firstChild);
    } else {
      this.grid.appendChild(fragment);
    }

    if (typeof window.initProductCards === 'function') {
      window.initProductCards(this.grid);
    }
  }

  _setNextUrl(nextUrl) {
    this.nextUrl = nextUrl || null;
    if (this.nextUrl) {
      this.dataset.nextUrl = this.nextUrl;
    } else {
      delete this.dataset.nextUrl;
    }
  }

  _savedState() {
    const state = history.state?.infiniteScroll;
    if (!state || typeof state !== 'object') return null;

    return {
      ...state,
      loadedUntilPage: parseInt(state.loadedUntilPage, 10) || 1,
    };
  }

  _saveState(partial = {}) {
    try {
      const current = history.state && typeof history.state === 'object' ? history.state : {};
      const prev =
        current.infiniteScroll && typeof current.infiniteScroll === 'object' ? current.infiniteScroll : {};
      const pageUrl = this._pageUrl(this._loadedUntilPage);

      history.replaceState(
        {
          ...current,
          collectionUrl: pageUrl,
          infiniteScroll: {
            ...prev,
            loadedUntilPage: this._loadedUntilPage,
            nextUrl: this.nextUrl || null,
            scrollY: window.scrollY,
            ...partial,
          },
        },
        '',
        pageUrl
      );
    } catch (err) {
      console.error('[collection-infinite-scroll] could not save pagination state', err);
    }
  }

  _pageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(page));
    }
    return `${url.pathname}${url.search}`;
  }

  _urlForPage(page) {
    const template = this.nextUrl || window.location.href;
    const url = new URL(template, window.location.origin);
    url.searchParams.delete('sections');
    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(page));
    }
    return `${url.pathname}${url.search}`;
  }

  _onGridClick(event) {
    const card = event.target.closest('[data-product-id]');
    if (!card || !this.grid.contains(card)) return;

    this._saveState({
      productId: card.dataset.productId,
      scrollY: window.scrollY,
    });
  }

  _onPageHide() {
    this._saveState({ scrollY: window.scrollY });
  }

  _buildSectionUrl(pageUrl) {
    const url = new URL(pageUrl, window.location.origin);
    url.searchParams.set('sections', this.sectionId);
    return `${url.pathname}${url.search}`;
  }

  _finishPagination() {
    this._observer?.disconnect();
    this._hideLoadControls();
    this._announce('Alle producten geladen');
  }

  _notifySectionLoad() {
    const sectionEl = document.getElementById(`shopify-section-${this.sectionId}`);
    if (sectionEl) {
      sectionEl.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    }
  }

  _setLoading(isLoading) {
    if (this.loadingEl) this.loadingEl.hidden = !isLoading;
    if (this.loadMoreBtn) this.loadMoreBtn.disabled = isLoading;
  }

  _showLoadMoreButton() {
    const more = this.querySelector('[data-infinite-scroll-more]');
    if (more) more.hidden = !this.nextUrl;
    if (this.loadMoreBtn) this.loadMoreBtn.hidden = !this.nextUrl;
    this._updateProgress();
  }

  _updatePageLabel() {
    this._updateProgress();
  }

  _updateProgress() {
    const total = parseInt(this.dataset.totalProducts, 10);
    const pageSize = parseInt(this.dataset.pageSize, 10) || 1;
    if (!total) return;

    const shown = Math.min(this._loadedUntilPage * pageSize, total);
    const shownEl = this.querySelector('[data-infinite-scroll-shown]');
    if (shownEl) shownEl.textContent = String(shown);

    const bar = this.querySelector('[data-infinite-scroll-progress]');
    const fill = this.querySelector('[data-infinite-scroll-progress-fill]');
    const percent = Math.max(0, Math.min(100, (shown / total) * 100));
    if (bar) {
      bar.setAttribute('aria-valuenow', String(shown));
      bar.setAttribute('aria-valuemax', String(total));
    }
    if (fill) fill.style.width = `${percent}%`;
  }

  _hideLoadControls() {
    const more = this.querySelector('[data-infinite-scroll-more]');
    if (more) more.hidden = true;
    if (this.loadMoreBtn) this.loadMoreBtn.hidden = true;
    if (this.loadingEl) this.loadingEl.hidden = true;
    this._updateProgress();
  }

  _showError() {
    if (this.errorEl) this.errorEl.hidden = false;
  }

  _hideError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }

  _announce(message) {
    if (this.statusEl) this.statusEl.textContent = message;
  }
}

if (!customElements.get('collection-infinite-scroll')) {
  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}
